'use client';

/**
 * renderers/pricing-block.tsx — extracted from block-renderer.tsx (SPEC-244 IA16-LANDING-FE).
 * Cero cambios de lógica — VERBATIM extraction. La composición la hace
 * el dispatcher block-renderer.tsx (importa los renderers por tipo).
 * Spec ref: SPEC-059 (docs/SPEC-059-LANDING-PAGE-BUILDER.md).
 *
 * [PR #1494 — hydration mismatch defensive measure] Added `'use client'`
 * directive at line 1. Doesn't fix the hydration mismatch alone (Client
 * Components are still SSR'd by Next.js), but marks the component boundary
 * cleanly for future maintainability. The root-cause fix is the BE
 * read-time normalization in `backend/src/features/landing/helpers.ts:
 * normalizeLandingBlocksSameOriginLinks` which guarantees the renderer
 * never sees an absolute same-origin URL. The `typeof window !==
 * 'undefined'` guard below remains as a defensive layer in case BE
 * normalization is bypassed (e.g. a custom-domain handler that returns
 * blocks raw).
 */

import * as React from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { IconByName } from '../../lucide-icons';
import { readableTextOn } from '../_helpers';
import type {
  PricingBlockData,
} from '@/lib/landing-types';

/**
 * Pricing block — grid of plan cards (name + price + features list + CTA).
 * Spec ref: SPEC-131 §3.2 C.1.
 */
export function PricingBlock({ data, bg }: { data: PricingBlockData; bg?: string }) {
  // [Pendiente #15] Per-block dark theme override — see features-block.tsx
  // for the full comment. Reuses `readableTextOn` from _helpers.ts
  // (anti-P-382).
  const mutedTextClass =
    bg && readableTextOn(bg) === 'text-white' ? 'text-white/85' : 'text-foreground/85';
  const plans = Array.isArray(data.plans) ? data.plans : [];
  if (plans.length === 0) {
    return (
      <section className="py-8 px-4">
        <div className="mx-auto max-w-4xl rounded-md border border-dashed border-border/50 p-8 text-center text-sm text-muted-foreground">
          Sin planes
        </div>
      </section>
    );
  }
  const cols = data.columns ?? (plans.length >= 3 ? 3 : plans.length >= 2 ? 2 : 1);
  const colClass =
    cols === 1 ? 'grid-cols-1 max-w-md' :
    cols === 2 ? 'grid-cols-1 sm:grid-cols-2 max-w-3xl' :
    'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl';
  return (
    <section className="py-12 px-4">
      <div className={cn('mx-auto grid gap-4', colClass)}>
        {plans.map((plan, i) => {
          const isHighlighted = plan.highlighted === true;
          // P-244 (SPEC-143): "MAYOR AHORRO" badge mirrors the public landing
          // PACKAGES Diamante badge (`bg-amber-500 text-white`). Same Tailwind
          // classes as the landing page.tsx line 479 — `bg-amber-500` +
          // `text-white`. The badge sits next to (after) the "Más popular"
          // badge so a plan can carry BOTH (e.g., a plan that is both the most
          // popular AND the best value). When both are set, the badges are
          // offset horizontally (Más popular left, MAYOR AHORRO right) so
          // they sit side-by-side instead of stacking on the same x.
          const isBestValue = plan.isBestValue === true;
          const ctaLink = typeof plan.ctaLink === 'string' ? plan.ctaLink : '';
          const isHttp = /^https?:\/\//i.test(ctaLink);
          const isAnchor = ctaLink.startsWith('#');
          // Anti-P-382 fix (2026-10-28): detect same-origin HTTP links
          // and treat them as internal (no _blank — fixes Safari/macOS).
          const isSameOriginHttp = isHttp && typeof window !== 'undefined' && (
            ctaLink.startsWith(window.location.origin + '/') ||
            ctaLink.startsWith(window.location.origin)
          );
          let resolvedLink = ctaLink;
          if (isSameOriginHttp && typeof window !== 'undefined') {
            resolvedLink = ctaLink.slice(window.location.origin.length) || '/';
          }
          const isInternal = (resolvedLink.startsWith('/') && !resolvedLink.startsWith('//')) || isSameOriginHttp;
          const hasValidLink = ctaLink && (isHttp || isAnchor || isInternal);
          return (
            <div
              key={i}
              className={cn(
                'relative flex flex-col rounded-2xl border bg-card p-6 shadow-sm transition-colors',
                isHighlighted
                  ? 'border-primary shadow-lg ring-2 ring-primary/30'
                  : 'border-border/50 hover:border-primary/40',
              )}
            >
              {isHighlighted ? (
                <span
                  className={cn(
                    'absolute -top-3 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-primary-foreground',
                    isBestValue ? 'left-2 -translate-x-0' : 'left-1/2 -translate-x-1/2',
                  )}
                >
                  Más popular
                </span>
              ) : null}
              {isBestValue ? (
                // P-244 (SPEC-143): "MAYOR AHORRO" badge — `bg-amber-500
                // text-white` matches the public landing page.tsx Diamante
                // badge (SPEC-142 PR #284). When `isHighlighted` is also
                // true, this badge is offset to the right (so the two
                // badges sit side-by-side); otherwise centered.
                <span
                  className={cn(
                    'absolute -top-3 rounded-full bg-amber-500 px-3 py-0.5 text-xs font-semibold text-white',
                    isHighlighted ? 'right-2 translate-x-0' : 'left-1/2 -translate-x-1/2',
                  )}
                >
                  MAYOR AHORRO
                </span>
              ) : null}
              <h3 className="text-lg font-semibold text-foreground">{plan.name}</h3>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-3xl font-bold tracking-tight text-foreground">{plan.price}</span>
                {plan.period ? (
                  <span className={cn('text-sm', mutedTextClass)}>{plan.period}</span>
                ) : null}
              </div>
              {Array.isArray(plan.features) && plan.features.length > 0 ? (
                <ul className={cn('mt-4 space-y-2 text-sm', mutedTextClass)}>
                  {plan.features.map((f, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="mt-0.5 text-primary" aria-hidden>✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {plan.ctaText ? (
                <div className="mt-6 pt-2">
                  {hasValidLink ? (
                    isInternal ? (
                      <Link
                        href={resolvedLink as any}
                        className={cn(
                          'inline-flex w-full items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors',
                          'bg-primary text-primary-foreground hover:bg-primary/90',
                        )}
                      >
                        {plan.ctaText}
                      </Link>
                    ) : (
                      <a
                        href={ctaLink}
                        {...(isHttp ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        className={cn(
                          'inline-flex w-full items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors',
                          'bg-primary text-primary-foreground hover:bg-primary/90',
                        )}
                      >
                        {plan.ctaText}
                      </a>
                    )
                  ) : (
                    <span
                      className={cn(
                        'inline-flex w-full items-center justify-center rounded-md px-4 py-2 text-sm font-medium',
                        // Same as the <a> above — primary variant for visibility.
                        'bg-primary text-primary-foreground',
                      )}
                    >
                      {plan.ctaText}
                    </span>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
